import React from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Award, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { ASSETS } from "@/lib/assets";

const SHARE_BASE = "https://robbedcoin.com";

function ChainTag({ type }) {
  const sol = type === "solana";
  return <span className={`tag !text-[9px] !px-2 !py-0.5 ${sol ? "bg-grape text-cream" : "bg-neon text-black"}`}>{sol ? "SOLANA" : "EVM"}</span>;
}

export default function PublicProfile() {
  const { userId } = useParams();
  const { data: p, isLoading, isError } = useQuery({
    queryKey: ["profile", userId], queryFn: async () => (await api.get(`/users/${userId}`)).data, retry: false,
  });

  if (isLoading) return <p className="font-mono text-dim max-w-5xl mx-auto px-6 py-16">Loading...</p>;
  if (isError || !p) return (
    <div className="max-w-md mx-auto px-4 py-20 text-center">
      <img src={ASSETS.shrugging} alt="" className="w-36 mx-auto" />
      <h1 className="headline text-cream text-2xl mt-4">Member not found.</h1>
      <Link to="/wall" className="btn-neon mt-6 inline-flex">Back To The Wall</Link>
    </div>
  );
  const initial = (p.displayName || "R").charAt(0).toUpperCase();
  const st = p.stats || {};
  const content = [...(p.memes || []), ...(p.stories || [])];
  const badges = p.badges || [];

  const winsLine = st.wins ? ` with ${st.wins} battle win${st.wins > 1 ? "s" : ""}` : "";
  const shareUrl = `${SHARE_BASE}/u/${p.user_id}`;
  const shareText = `I'm ROB LEVEL ${p.level} · ${p.levelName} in the $ROBBED army${winsLine}. THEY GOT RICH. WE GOT ROBBED. 👉`;
  const shareToX = () => {
    const url = `https://x.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}&via=robbedcoin`;
    window.open(url, "_blank", "noopener,noreferrer,width=600,height=500");
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16">
      <Link to="/wall" className="flex items-center gap-1 font-mono text-xs text-dim hover:text-neon"><ArrowLeft className="w-3.5 h-3.5" /> Back</Link>
      <div className="comic-card p-6 sm:p-8 mt-4 relative overflow-hidden">
        <div className="halftone absolute inset-0 opacity-30" />
        <div className="relative flex flex-col sm:flex-row gap-6 items-start">
          {p.picture
            ? <img src={p.picture} alt="avatar" className="w-24 h-24 rounded-full border-3 border-neon" />
            : <span className="w-24 h-24 rounded-full bg-neon text-black font-display font-900 flex items-center justify-center text-4xl">{initial}</span>}
          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-display font-900 text-cream text-3xl">{p.displayName}</h1>
              <span className="inline-flex items-center gap-1 border-2 border-neon text-neon px-2 py-0.5 font-heading font-bold uppercase text-[10px]" data-testid="profile-rob-level">
                <Award className="w-3.5 h-3.5" /> ROB LEVEL {p.level} · {p.levelName}
              </span>
              <span className="font-mono text-xs text-dim">{p.rep} REP</span>
            </div>
            {p.username && <p className="font-mono text-neon text-sm">@{p.username}</p>}
            <p className="font-mono text-dim text-sm mt-3">{p.bio || "This member of the Robbed hasn't written a bio yet."}</p>
            <div className="flex flex-wrap gap-2 mt-4">
              {(p.chains || []).map((c) => <ChainTag key={c} type={c} />)}
              {(p.authProviders || []).includes("google") && <span className="tag !text-[9px]">Google</span>}
            </div>
            <button onClick={shareToX} className="btn-neon mt-5 !py-2 !px-4 !text-xs inline-flex" data-testid="share-profile-x-btn">
              <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current" aria-hidden="true">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
              Flex On X
            </button>
          </div>
        </div>

        <div className="relative grid grid-cols-3 sm:grid-cols-6 gap-2 mt-6">
          {[["REP", p.rep], ["Memes", st.memesTotal], ["Votes", st.votesReceived],
            ["Finalists", st.finalists], ["Wins", st.wins], ["Faves", st.favorites]].map(([l, v]) => (
            <div key={l} className="border-3 border-black bg-ink p-2 text-center">
              <p className="font-display font-900 text-neon text-lg">{v ?? 0}</p>
              <p className="font-mono text-[9px] uppercase text-dim">{l}</p>
            </div>
          ))}
        </div>

        {badges.length > 0 && (
          <div className="relative mt-5">
            <p className="font-mono text-[10px] uppercase text-dim mb-2">Badges Earned</p>
            <div className="flex flex-wrap gap-2" data-testid="profile-badges">
              {badges.map((b) => (
                <span key={b.id} title={b.desc}
                  className="inline-flex items-center gap-1 border-2 border-neon text-neon bg-neon/10 px-2 py-1 font-heading font-bold uppercase text-[10px] leading-none">
                  {b.icon} {b.name}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {content.length > 0 && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
          {content.map((s) => (
            <div key={s.id} className="comic-card p-4" data-testid={`pub-${s.id}`}>
              {s.image_url && <img src={`${process.env.REACT_APP_BACKEND_URL}${s.image_url}`} alt="meme" className="w-full max-h-64 object-contain border-3 border-black bg-white" />}
              {s.story && <p className="font-mono text-sm text-cream/90 mt-3">"{s.story}"</p>}
              <p className="font-mono text-[10px] text-neon mt-3">▲ {s.upvotes}{s.isFavorite ? " · 🔥 Community Favorite" : ""}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
